import {
  Controller,
  useFieldArray,
  useFormState,
  useWatch,
  type Control,
} from 'react-hook-form';
import { PlusIcon, Trash2Icon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatMoney } from '@/lib/utils';

import { lineItemsTotal, MAX_LINE_ITEMS } from './helpers';
import type { QuoteFieldsValues } from './schema';

interface Props {
  // Any quote form that spreads `quoteFieldsSchema` — add + edit.
  control: Control<QuoteFieldsValues>;
}

const EMPTY_ROW = { description: '', amount: '' };

// Shared line-items editor + total control. Bound to the parent RHF form via
// `control`; the parent maps `lineItems` → `QuoteLineItem[]` (`toLineItems`) and
// resolves `total_amount` from `manualTotal` on submit.
export function LineItemsField({ control }: Props) {
  const { t } = useLanguage();

  const { fields, append, remove } = useFieldArray({ control, name: 'lineItems' });
  const rows = useWatch({ control, name: 'lineItems' }) ?? [];
  const manualTotal = useWatch({ control, name: 'manualTotal' });
  const subtotal = lineItemsTotal(rows);

  const { errors } = useFormState({ control, name: 'lineItems' });
  const listError =
    (errors.lineItems as { message?: string; root?: { message?: string } } | undefined) ?? {};
  const listErrorMessage = listError.message ?? listError.root?.message;

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <Label>{t.quoteLineItems}</Label>
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={fields.length >= MAX_LINE_ITEMS}
          data-testid="line-item-add"
          onClick={() => append({ ...EMPTY_ROW })}
        >
          <PlusIcon className="w-3 h-3" />
          {t.quoteAddLineItem}
        </Button>
      </div>

      {listErrorMessage && (
        <p className="text-xs text-destructive" data-testid="line-items-error">
          {listErrorMessage}
        </p>
      )}

      {fields.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t.quoteNoLineItems}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {fields.map((field, index) => (
            <li key={field.id} className="flex items-start gap-2">
              <Controller
                control={control}
                name={`lineItems.${index}.description`}
                render={({ field: f, fieldState }) => (
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <Input
                      {...f}
                      aria-label={t.quoteLineItemDescription}
                      placeholder={t.quoteLineItemDescriptionPlaceholder}
                      aria-invalid={fieldState.invalid}
                      data-testid={`line-item-desc-${index}`}
                    />
                    {fieldState.error && (
                      <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    )}
                  </div>
                )}
              />
              <Controller
                control={control}
                name={`lineItems.${index}.amount`}
                render={({ field: f, fieldState }) => (
                  <div className="flex flex-col gap-1 w-28">
                    <Input
                      {...f}
                      inputMode="decimal"
                      aria-label={t.quoteLineItemAmount}
                      placeholder="0.00"
                      aria-invalid={fieldState.invalid}
                      data-testid={`line-item-amount-${index}`}
                    />
                    {fieldState.error && (
                      <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    )}
                  </div>
                )}
              />
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={t.quoteRemoveLineItem}
                data-testid={`line-item-remove-${index}`}
                className="text-muted-foreground hover:text-destructive transition-colors rounded-md p-2"
              >
                <Trash2Icon className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between text-sm border-t pt-2">
        <span className="text-muted-foreground">{t.quoteLineItemsSubtotal}</span>
        <span data-testid="line-items-subtotal">{formatMoney(subtotal)}</span>
      </div>

      <Controller
        control={control}
        name="manualTotal"
        render={({ field }) => (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
              ref={field.ref}
              data-testid="quote-manual-total-toggle"
              className="size-4 rounded border"
            />
            {t.quoteSetTotalManually}
          </label>
        )}
      />

      {manualTotal ? (
        <Controller
          control={control}
          name="totalAmount"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="quote-total">{t.quoteTotal}</Label>
              <Input
                {...field}
                id="quote-total"
                inputMode="decimal"
                placeholder="0.00"
                aria-invalid={fieldState.invalid}
                data-testid="quote-total-input"
                className="w-40"
              />
              {fieldState.error && (
                <p className="text-xs text-destructive">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />
      ) : (
        <div className="flex items-center justify-between text-sm font-semibold">
          <span>{t.quoteTotal}</span>
          <span data-testid="quote-total-display">{formatMoney(subtotal)}</span>
        </div>
      )}
    </div>
  );
}
